import { Plus, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface Competitor {
  name: string;
  url: string;
}

interface CompetitorsFieldProps {
  competitors: Competitor[];
  onUpdate: (competitors: Competitor[]) => void;
  errors?: string;
  defaultCompetitors?: Competitor[];
  onReset?: () => void;
}

export function CompetitorsField({
  competitors,
  onUpdate,
  errors,
  defaultCompetitors,
  onReset,
}: CompetitorsFieldProps) {
  const handleAdd = () => {
    onUpdate([...competitors, { name: "", url: "" }]);
  };

  const handleRemove = (index: number) => {
    onUpdate(competitors.filter((_, i) => i !== index));
  };
  const showResetButton =
    defaultCompetitors && defaultCompetitors.length > 0 && onReset;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Competitors</Label>
        <div className="flex gap-2">
          {showResetButton && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Reset competitors to account defaults"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-1" />
            Add Competitor
          </Button>
        </div>
      </div>

      {competitors.map((competitor, index) => (
        <div key={index} className="flex gap-2 items-start">
          <div className="flex-1 space-y-2">
            <Input
              placeholder="Competitor name"
              value={competitor.name}
              onChange={(e) => {
                const updated = competitors.map((c, i) =>
                  i === index ? { ...c, name: e.target.value } : c,
                );
                onUpdate(updated);
              }}
            />
          </div>
          <div className="flex-1 space-y-2">
            <Input
              type="url"
              placeholder="https://www.example.com"
              value={competitor.url}
              onChange={(e) => {
                const updated = competitors.map((c, i) =>
                  i === index ? { ...c, url: e.target.value } : c,
                );
                onUpdate(updated);
              }}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => handleRemove(index)}
            data-testid={`remove-competitor-${index}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {errors && <p className="text-sm text-destructive">{errors}</p>}
    </div>
  );
}
